import {
    DomainType,
    ModalHook,
    UIAction,
    useAppNavigation
} from "@/Shared";

import { DOMAIN_CONFIGS } from "@/Shared/Config/domain-config";
import { AccessRightModel } from "@/Domains";
import GenericDomainForm from "@/Shared/UI/Forms/GenericDomainForm";

/**
 * Properties for generating UI actions for a domain entity.
 */
export interface UIActionProps {
    /** Optional ID of the current entity instance */
    id?: number;

    /** Domain type for which actions are being generated */
    domainType: DomainType;

    /** User's access rights for the current domain */
    accessRight: AccessRightModel;

    /** Whether the UI is currently in form mode */
    isForm?: boolean;

    /** Whether the entity is currently deleted */
    isDeleted?: boolean;

    /** Optional function to execute deletion of the entity */
    executeDelete?: (entityId: string | number) => Promise<void>;

    /** Optional function to execute restoration of the entity */
    executeRestore?: (entityId: string | number) => Promise<void>;

    /** Optional flag indicating delete operation is in progress */
    deleteLoading?: boolean;

    /** Optional flag indicating restore operation is in progress */
    restoreLoading?: boolean;

    modalHook?: ModalHook;
    inModal: boolean;
}

/**
 * =====================================================
 * useUIActions
 * =====================================================
 *
 * Generates UI actions for a given domain entity based on
 * access rights, entity state, and configuration.
 *
 * Responsibilities:
 * - Compute header, global, and contextual actions.
 * - Respect access rights for edit, delete, restore, and create operations.
 * - Handle navigation either via modal or route.
 * - Resolve labels and configuration from DOMAIN_CONFIGS.
 *
 * Notes:
 * - Purely UI-focused; no side effects beyond modal opening or navigation.
 * - Contextual buttons automatically inject parent entity into extraArgs.
 *
 * Author: Samano CASTRE
 * Date: February 28, 2026
 *
 * @param props - UIActionProps containing domain information and access state
 * @returns Methods to compute UI actions for the UI layer
 */
export const useUIActions = (props: UIActionProps) => {

    const {
        inModal,
        modalHook,
        domainType,
        accessRight,
        id,
        isForm,
        isDeleted,
        executeDelete,
        executeRestore,
        deleteLoading,
        restoreLoading
    } = props;

    const { goToForm, goToSelector } = useAppNavigation();
    const {
        domainLabel,
        globalButtons,
        contextualButtons,
        desableDelete,
    } = DOMAIN_CONFIGS[domainType];

    const canWriteDomain = accessRight?.canWrite(domainType);
    const canDeleteDomain = accessRight?.canDelete(domainType);
    const canRestoreDomain = accessRight?.canRestore(domainType);

    /**
     * Computes all UI actions for this domain instance,
     * combining header, global, and contextual actions.
     *
     * @param model Optional parent model for contextual actions
     * @returns Array of UIAction objects
     */
    const computeUIActions = (model?: any): UIAction[] => {
        const actions: UIAction[] = [];
        if (!isForm && !id) {
            return actions;
        }
        return [
            ...computeHeaderUIActions(),
            ...computeGlobalUIActions(),
            ...computeContextualUIActions(model)
        ];
    }


    /**
     * Computes header actions such as Edit, Delete, Restore
     *
     * @returns Array of header UIAction objects
     */
    const computeHeaderUIActions = () => {
        const actions: UIAction[] = [];
        if (id && !isForm && !isDeleted) {
            actions.push({
                key: `${domainType}-navigation-${id}`,
                label: "Modifier",
                onClick: () => canWriteDomain ? handleNavClick(domainType, id) : undefined,
                placement: "header",
                responsive: true,
                type: 'default',
                icon: 'FaEdit',
                disabled: !canWriteDomain,
                tooltip: canWriteDomain
                    ? `Cliquez pour modifier "${domainLabel}".`
                    : `Vous n'avez pas les droits suffisants pour modifier "${domainLabel}".`

            })
        }

        if (id && !desableDelete) {
            if (!isDeleted && executeDelete) {
                actions.push({
                    key: `${domainType}-execute-delete-${id}`,
                    label: "Supprimer",
                    onClick: () => canDeleteDomain ? executeDelete(id) : undefined,
                    placement: "header",
                    responsive: true,
                    type: 'default',
                    icon: 'FaTrash',
                    disabled: !canDeleteDomain,
                    loading: deleteLoading,
                    tooltip: canDeleteDomain
                        ? `Cliquez pour supprimer "${domainLabel}".`
                        : `Vous n'avez pas les droits suffisants pour supprimer "${domainLabel}".`
                })
            }
            else if (isDeleted && executeRestore) {
                actions.push({
                    key: `${domainType}-execute-restore-${id}`,
                    label: "Restaurer",
                    onClick: () => canRestoreDomain ? executeRestore(id) : undefined,
                    placement: "header",
                    responsive: true,
                    type: 'default',
                    icon: 'FaRedo',
                    disabled: !canRestoreDomain,
                    loading: restoreLoading,
                    tooltip: canRestoreDomain
                        ? `Cliquez pour restaurer "${domainLabel}".`
                        : `Vous n'avez pas les droits suffisants pour restaurer "${domainLabel}".`
                })
            }
        }
        return actions;
    }

    /**
     * Computes global actions for related domain creation buttons.
     *
     * @returns Array of sub-navigation UIAction objects
     */
    const computeGlobalUIActions = () => {
        const actions: UIAction[] = [];
        (globalButtons ?? []).forEach(buttonType => {
            const {
                domainLabel: buttonLabel,
                prohibitCreate: buttonProhibitCreate,
                DomainFormSelector: buttonDomainFormSelector
            } = DOMAIN_CONFIGS[buttonType];

            if (buttonProhibitCreate) {
                return;
            }

            const canWriteCreateButton = accessRight?.canWrite(buttonType);

            actions.push({
                key: `${buttonType}-navigation`,
                label: buttonLabel,
                onClick: () => canWriteCreateButton
                    ? buttonDomainFormSelector
                        ? handleSelectorClick(buttonDomainFormSelector, buttonType)
                        : handleNavClick(buttonType)
                    : undefined,
                placement: "sub",
                type: 'default',
                icon: 'FaPlusCircle',
                disabled: !canWriteCreateButton,
                tooltip: canWriteCreateButton
                    ? `Cliquez pour créer 1 "${buttonLabel}".`
                    : `Vous n'avez pas les droits suffisants pour créer 1 "${buttonLabel}".`
            });

        });
        return actions;
    }

    /**
     * Computes contextual actions that require the current entity as parent.
     *
     * @param model Parent entity model for contextual creation
     * @returns Array of contextual UIAction objects
     */
    const computeContextualUIActions = (model?: any) => {
        const actions: UIAction[] = [];
        if (!model) {
            return actions;
        }
        (contextualButtons ?? []).forEach(buttonType => {
            const {
                domainLabel: buttonLabel,
                prohibitCreate: buttonProhibitCreate,
                DomainFormSelector: buttonDomainFormSelector
            } = DOMAIN_CONFIGS[buttonType];

            if (buttonProhibitCreate) {
                return;
            }

            const canWriteCreateButton = accessRight?.canWrite(buttonType);

            actions.push({
                key: `${buttonType}-contextual`,
                label: buttonLabel,
                onClick: () => canWriteCreateButton
                    ? displayInModal(model, buttonType, buttonLabel, buttonDomainFormSelector)
                    : undefined,
                placement: "sub",
                type: 'context',
                icon: 'FaPlusCircle',
                disabled: !canWriteCreateButton,
                tooltip: canWriteCreateButton
                    ? `Cliquez pour créer 1 "${buttonLabel}" pour "${domainLabel}" - (ID : ${model.id ?? '-'}).`
                    : `Vous n'avez pas les droits suffisants pour créer 1 "${buttonLabel}" .`
            });

        });
        return actions;
    }

    /**
     * Opens a domain form in a modal, optionally using a selector component.
     *
     * @param model Parent entity model to inject into extraArgs
     * @param buttonType Type of the domain to create
     * @param buttonLabel Label to display in the modal title
     * @param ButtonSelector Optional selector component to use before form
     */
    const displayInModal = (
        model: any,
        buttonType: DomainType,
        buttonLabel: string,
        ButtonSelector?: React.ElementType<any, keyof React.JSX.IntrinsicElements>) => {

        if (!modalHook) {
            console.warn("Display in modal requested without modalHook.");
            return;
        }

        const argKey = domainType.toLocaleLowerCase();
        const content = ButtonSelector
            ? <ButtonSelector inModalHook={modalHook} />
            : <GenericDomainForm
                domainType={buttonType}
                inModalHook={modalHook}
                extraArgs={{
                    [argKey]: model
                }}
            />;

        return modalHook.openModal({
            title: `${buttonLabel} pour ${domainLabel} - (ID : ${model.id ?? '-'})`,
            content
        });
    }



    /**
     * Handles navigation to a form, either via modal or route.
     *
     * @param domainType Type of the domain to navigate to
     * @param id Optional entity ID for editing
     */
    const handleNavClick = (domainType: DomainType, id?: number) => {
        if (inModal) {
            if (!modalHook) {
                console.warn("Display in modal requested without modalHook.");
                return;
            }
            modalHook.openModal({
                title: `${id ? "Modification" : "Création"}`,
                content: <GenericDomainForm
                    domainType={domainType}
                    inModalHook={modalHook}
                    extraArgs={{
                        entityId: id
                    }}
                />
            });
        }
        else {
            goToForm(domainType, id);
        }
    }

     /**
     * Handles navigation to a selector, either via modal or route.
     *
     * @param DomainSelector Component to render the selection step
     * @param domainType Type of domain associated with the selector
     */
    const handleSelectorClick = (
        DomainSelector: React.ElementType<any, keyof React.JSX.IntrinsicElements>,
        domainType: DomainType) => {

        if (inModal) {
            if (!modalHook) {
                console.warn("Display in modal requested without modalHook.");
                return;
            }
            modalHook.openModal({
                title: "Choix",
                content: <DomainSelector inModalHook={modalHook} />
            });
        }
        else {
            goToSelector(domainType);
        }
    }

    /**
     * Public API for computing UI actions.
     */
    return {
        computeUIActions,
        computeHeaderUIActions,
        computeGlobalUIActions,
        computeContextualUIActions
    }
}